// GIF/Lottie/image compositing for node cache canvas

import { parseGIF, decompressFrames } from 'gifuct-js';
import type { ParsedFrame } from 'gifuct-js';
import { DotLottie } from '@lottiefiles/dotlottie-web';
import useShapeUtils from '@/modules/Node/utils/shapeUtils';
import type { GifDecodeResult, ImageBounds } from '@/modules/Node';
import { getMapAreaService } from '@/modules/map-editor/composables/useMapArea';

const { addShapeToContext } = useShapeUtils();

// Shared caches — prevent N nodes from issuing N fetches for the same URL
const GIF_DECODE_CACHE = new Map<string, Promise<GifDecodeResult | null>>();
type LottieData = ArrayBuffer | string;
const LOTTIE_DATA_CACHE = new Map<string, Promise<LottieData | null>>();
const LOTTIE_URL_PATTERN = /\.(lot|lottie|lottie\.json|json)$/i;

interface MediaRenderState {
  id: number | undefined;
  shape: string;
  width: number;
  height: number;
  border: boolean;
  borderWidth: number;
  media: string | null;
  mediaURL: string | null;
  image: HTMLImageElement | HTMLCanvasElement | null;
  mediaCropEnabled: boolean;
  mediaOpacity: number;
  gifAnimation: boolean;
  globalAlpha: number;
  xCache: number | null;
  yCache: number | null;
  shiftX: number | null;
  shiftY: number | null;
  imageBounds: ImageBounds | null;
}

export class NodeMediaRenderer {
  // GIF state
  animation: boolean = false;
  frames: ParsedFrame[] = [];
  frameIndex: number = 0;
  frameCanvas: HTMLCanvasElement | null = null;
  frameCtx: CanvasRenderingContext2D | null = null;
  frameCommonCanvas: HTMLCanvasElement | null = null;
  frameCommonCtx: CanvasRenderingContext2D | null = null;
  framesSource: string | null = null;
  frameTimeout: ReturnType<typeof setTimeout> | null = null;
  _frameImageData: ImageData[] = [];
  gifWidth: number = 0;
  gifHeight: number = 0;
  private prevDisposal: { type: number; dims: { left: number; top: number; width: number; height: number } } | null = null;

  // Lottie state
  lottieInstance: DotLottie | null = null;
  lottieCanvas: HTMLCanvasElement | null = null;
  lottieSource: string | null = null;
  lottieReady: boolean = false;

  destroy(): void {
    this.destroyLottie();
    if (this.frameTimeout) clearTimeout(this.frameTimeout);
  }

  destroyLottie(): void {
    if (this.lottieInstance) {
      this.lottieInstance.destroy();
      this.lottieInstance = null;
    }
    this.lottieCanvas = null;
    this.lottieSource = null;
    this.lottieReady = false;
  }

  async drawMedia(ctx: CanvasRenderingContext2D, state: MediaRenderState): Promise<void> {
    const { x, y, width, height } = state.imageBounds!;
    const hostURL = import.meta.env.VITE_ROOT_HOST;
    const media = state.media && !state.media.includes(hostURL!) ? `${hostURL}${state.media}` : state.media;
    const url = media || state.mediaURL;
    const isGifURL = url?.includes('.gif');
    const isLottieURL = LOTTIE_URL_PATTERN.test(url ?? '');

    // Lottie animation handling
    if (state.id !== undefined && isLottieURL &&
      (!this.lottieInstance || this.lottieSource !== url)) {

      this.destroyLottie();

      if (!LOTTIE_DATA_CACHE.has(url!)) {
        LOTTIE_DATA_CACHE.set(url!, (async (): Promise<LottieData | null> => {
          const response = await fetch(url!, { method: 'GET', credentials: 'include' });
          if (!response.ok) throw new Error('Network response was not ok');
          const contentType = response.headers.get('content-type') ?? '';
          // Plain Lottie JSON → pass as string; dotLottie binary → pass as ArrayBuffer
          if (contentType.includes('json') || url!.endsWith('.json')) {
            const text = await response.text();
            if (!text) return null;
            return text;
          }
          const buff = await response.arrayBuffer();
          if (buff.byteLength === 0) return null;
          return buff;
        })().catch(err => {
          console.error('Error loading Lottie:', err);
          LOTTIE_DATA_CACHE.delete(url!);
          return null;
        }));
      }

      try {
        const data = await LOTTIE_DATA_CACHE.get(url!)!;
        if (data) {
          this.lottieCanvas = document.createElement('canvas');
          this.lottieCanvas.width = width!;
          this.lottieCanvas.height = height!;
          this.lottieSource = url!;

          const nodeId = state.id;
          this.lottieInstance = new DotLottie({
            canvas: this.lottieCanvas,
            data: data instanceof ArrayBuffer ? data.slice(0) : data,
            autoplay: true,
            loop: true,
            renderConfig: { autoResize: false, freezeOnOffscreen: false },
          });

          this.lottieInstance.addEventListener('render', () => {
            if (nodeId !== undefined) getMapAreaService().updateMediaOnly(nodeId);
          });

          this.lottieInstance.addEventListener('load', () => {
            this.lottieReady = true;
          });

          this.animation = true;
        }
      } catch (err) {
        console.error('Error applying Lottie:', err);
      }
    }

    // Resize lottie canvas if node dimensions changed
    if (this.lottieReady && this.lottieCanvas &&
      (this.lottieCanvas.width !== width! || this.lottieCanvas.height !== height!)) {
      this.lottieCanvas.width = width!;
      this.lottieCanvas.height = height!;
      this.lottieInstance?.resize();
    }

    // Clean up lottie if media changed to non-lottie
    if (!isLottieURL && this.lottieInstance) {
      this.destroyLottie();
    }

    // For non-lottie media, require a loaded image
    if (!isLottieURL && (!state.image || !state.image.width)) return;

    // GIF animation handling
    if (state.id !== undefined && isGifURL &&
      (!this.animation || (this.framesSource && url !== this.framesSource))) {

      if (!GIF_DECODE_CACHE.has(url!)) {
        GIF_DECODE_CACHE.set(url!, (async (): Promise<GifDecodeResult | null> => {
          const response = await fetch(url!, { method: 'GET', credentials: 'include' });
          if (!response.ok) throw new Error('Network response was not ok');
          const buff = await response.arrayBuffer();
          if (buff.byteLength === 0) return null;
          const gif = parseGIF(buff);
          const frames = decompressFrames(gif, true);
          const frameImageData = frames.map(f => new ImageData(f.patch, f.dims.width, f.dims.height));
          return { frames, frameImageData, width: gif.lsd.width, height: gif.lsd.height };
        })().catch(err => {
          console.error('Error loading GIF:', err);
          GIF_DECODE_CACHE.delete(url!);
          return null;
        }));
      }

      try {
        const decoded = await GIF_DECODE_CACHE.get(url!)!;
        if (decoded) {
          this.frameIndex = 0;
          this.frames = decoded.frames;
          this._frameImageData = decoded.frameImageData;
          this.gifWidth = decoded.width;
          this.gifHeight = decoded.height;
          this.framesSource = url!;
          this.animation = true;

          this.frameCommonCanvas = null;
          this.frameCanvas = null;
          this.frameCommonCtx = null;
          this.frameCtx = null;
          this.prevDisposal = null;
        }
      } catch (err) {
        console.error('Error applying GIF:', err);
      }
    }

    // Reset animation if it's not GIF or lottie
    if (!isGifURL && !isLottieURL && this.animation) {
      this.animation = false;
    }

    ctx.save();
    try {
      ctx.globalAlpha = state.globalAlpha < 1 ? state.globalAlpha : (state.mediaOpacity / 100);

      if (state.mediaCropEnabled) {
        addShapeToContext({
          ctx,
          node: state,
          center: { x: state.xCache!, y: state.yCache! },
        });
        ctx.clip();
      }

      let image: HTMLImageElement | HTMLCanvasElement | null = isLottieURL ? null : state.image!;

      // Handling Lottie animation
      if (this.lottieReady && this.lottieCanvas) {
        image = this.lottieCanvas;
      }

      // Handling GIF animation
      if (!isLottieURL && this.animation && state.id !== undefined && state.gifAnimation && this.frames?.[this.frameIndex]) {
        const frame = this.frames[this.frameIndex];
        const start = performance.now();

        if (!this.frameCommonCtx) {
          this.frameCommonCanvas = document.createElement('canvas');
          this.frameCommonCtx = this.frameCommonCanvas.getContext('2d');
          this.frameCommonCanvas.width = this.gifWidth;
          this.frameCommonCanvas.height = this.gifHeight;
        }
        if (!this.frameCtx) {
          this.frameCanvas = document.createElement('canvas');
          this.frameCtx = this.frameCanvas.getContext('2d');
          this.frameCanvas.width = this.gifWidth;
          this.frameCanvas.height = this.gifHeight;
        }

        // Step 1: Apply PREVIOUS frame's disposal to prepare for current frame
        if (this.frameIndex === 0) {
          this.frameCommonCtx!.clearRect(0, 0, this.gifWidth, this.gifHeight);
        } else if (this.prevDisposal?.type === 2) {
          const d = this.prevDisposal.dims;
          this.frameCommonCtx!.clearRect(d.left, d.top, d.width, d.height);
        }

        // Step 2: Draw current frame patch onto composite
        const imageData = this._frameImageData[this.frameIndex];
        this.frameCtx!.clearRect(0, 0, this.gifWidth, this.gifHeight);
        this.frameCtx!.putImageData(imageData, frame.dims.left, frame.dims.top);
        this.frameCommonCtx!.drawImage(this.frameCanvas!, 0, 0);
        image = this.frameCommonCanvas!;

        // Step 3: Store current frame's disposal for next iteration
        this.prevDisposal = { type: frame.disposalType, dims: frame.dims };

        this.frameIndex = (this.frameIndex + 1) % this.frames.length;

        const diff = performance.now() - start;
        if (this.frameTimeout) clearTimeout(this.frameTimeout);

        this.frameTimeout = setTimeout(() => {
          if (state.id !== undefined) getMapAreaService().updateMediaOnly(state.id!);
        }, Math.max(0, Math.floor(frame.delay - diff)));
      }

      if (image) {
        ctx.drawImage(image, x! + state.shiftX!, y! + state.shiftY!, width!, height!);
      }
    } finally {
      ctx.restore();
    }
  }
}

export type { MediaRenderState };
